import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Undo as UndoIcon,
  Redo as RedoIcon
} from '@mui/icons-material';
import S3ApiService from '../../services/s3-api.service';

const CodeEditor = ({
  open,
  onClose,
  file,
  organizationName,
  sceneName,
  darkMode = true
}) => {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  // Get file extension and determine language
  const getLanguageFromFileName = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'js':
      case 'jsx':
      case 'mjs':
        return 'javascript';
      case 'ts':
      case 'tsx':
        return 'typescript';
      case 'html':
      case 'htm':
        return 'html';
      case 'css':
        return 'css';
      case 'scss':
      case 'sass':
        return 'scss';
      case 'json':
        return 'json';
      case 'xml':
        return 'xml';
      case 'py':
        return 'python';
      case 'php':
        return 'php';
      case 'java':
        return 'java';
      case 'cpp':
      case 'cc':
      case 'cxx':
        return 'cpp';
      case 'c':
        return 'c';
      case 'cs':
        return 'csharp';
      case 'go':
        return 'go';
      case 'rs':
        return 'rust';
      case 'rb':
        return 'ruby';
      case 'sh':
      case 'bash':
        return 'shell';
      case 'sql':
        return 'sql';
      case 'md':
      case 'markdown':
        return 'markdown';
      case 'yaml':
      case 'yml':
        return 'yaml';
      case 'toml':
        return 'toml';
      case 'ini':
      case 'cfg':
        return 'ini';
      default:
        return 'plaintext';
    }
  };

  useEffect(() => {
    if (open && file) {
      loadFileContent();
    }
  }, [open, file]);

  const loadFileContent = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      let fileKey = file.path;
      if (fileKey.startsWith('assets/')) {
        fileKey = fileKey.substring(6);
      } else if (fileKey === 'assets') {
        fileKey = '';
      }

      const signedUrl = await S3ApiService.getSignedUrl({
        key: fileKey,
        organizationName,
        sceneName,
        expiresIn: 3600
      });

      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const fileContent = await response.text();
      setContent(fileContent);
      setOriginalContent(fileContent);
    } catch (err) {
      console.error('Error loading file content:', err);
      setError(`Failed to load file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!file || !editorRef.current) return;

    setSaving(true);
    setError(null);

    try {
      const currentContent = editorRef.current.getValue();
      
      // Create a new file object with the updated content
      const updatedFile = new File([currentContent], file.name, {
        type: file.fileType || 'text/plain'
      });

      let fileKey = file.path;
      if (fileKey.startsWith('assets/')) {
        fileKey = fileKey.substring(6);
      } else if (fileKey === 'assets') {
        fileKey = '';
      }

      await S3ApiService.uploadObject({
        file: updatedFile,
        path: fileKey,
        organizationName,
        sceneName
      });

      setOriginalContent(currentContent);
      setSuccess(true);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving file:', err);
      setError(`Failed to save file: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!file) return;

    try {
      let fileKey = file.path;
      if (fileKey.startsWith('assets/')) {
        fileKey = fileKey.substring(6);
      } else if (fileKey === 'assets') {
        fileKey = '';
      }

      const signedUrl = await S3ApiService.getSignedUrl({
        key: fileKey,
        organizationName,
        sceneName,
        expiresIn: 3600
      });

      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(`Failed to download file: ${err.message}`);
    }
  };

  const handleUndo = () => {
    if (editorRef.current) {
      editorRef.current.trigger('keyboard', 'undo', null);
    }
  };

  const handleRedo = () => {
    if (editorRef.current) {
      editorRef.current.trigger('keyboard', 'redo', null);
    }
  };

  const hasChanges = () => {
    if (!editorRef.current) return false;
    return editorRef.current.getValue() !== originalContent;
  };

  const handleClose = () => {
    if (hasChanges()) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
  };

  const language = file ? getLanguageFromFileName(file.name) : 'plaintext';

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="90vw"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            bgcolor: darkMode ? '#1e1e1e' : '#ffffff'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: darkMode ? '#2d2d30' : '#f5f5f5',
            borderBottom: '1px solid',
            borderColor: darkMode ? '#3e3e42' : '#e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" noWrap sx={{ maxWidth: '400px' }}>
              {file?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({language})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Undo">
              <IconButton size="small" onClick={handleUndo}>
                <UndoIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Redo">
              <IconButton size="small" onClick={handleRedo}>
                <RedoIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save">
              <IconButton 
                size="small" 
                onClick={handleSave}
                disabled={saving || !hasChanges()}
                color="primary"
              >
                {saving ? <CircularProgress size={20} /> : <SaveIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, flexGrow: 1, overflow: 'hidden' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                p: 2
              }}
            >
              <Alert severity="error" sx={{ maxWidth: '500px' }}>
                {error}
              </Alert>
            </Box>
          ) : (
            <Editor
              height="100%"
              language={language}
              theme={darkMode ? 'vs-dark' : 'light'}
              value={content}
              onChange={(value) => setContent(value || '')}
              onMount={handleEditorDidMount}
              options={{
                minimap: { enabled: true },
                fontSize: 14,
                lineNumbers: 'on',
                roundedSelection: false,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: 'on',
                folding: true,
                foldingStrategy: 'indentation',
                showFoldingControls: 'always',
                renderWhitespace: 'selection',
                renderControlCharacters: false,
                renderLineHighlight: 'all',
                selectOnLineNumbers: true,
                glyphMargin: true,
                useTabStops: false,
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                tabSize: 2,
                insertSpaces: true,
                detectIndentation: true,
                trimAutoWhitespace: true,
                largeFileOptimizations: true,
                suggest: {
                  showKeywords: true,
                  showSnippets: true,
                  showClasses: true,
                  showFunctions: true,
                  showVariables: true,
                  showModules: true,
                  showProperties: true,
                  showEvents: true,
                  showOperators: true,
                  showUnits: true,
                  showValues: true,
                  showConstants: true,
                  showEnums: true,
                  showEnumMembers: true,
                  showColors: true,
                  showFiles: true,
                  showReferences: true,
                  showFolders: true,
                  showTypeParameters: true,
                  showWords: true
                }
              }}
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: darkMode ? '#2d2d30' : '#f5f5f5',
            borderTop: '1px solid',
            borderColor: darkMode ? '#3e3e42' : '#e0e0e0',
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {hasChanges() ? 'Modified' : 'Saved'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleClose} color="inherit">
                Close
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={saving || !hasChanges()}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(false)} severity="success">
          File saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default CodeEditor; 